import React, { useState, useEffect } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import Modal from '../../components/Layout/Modal';
import Pagination from '../../components/UI/Pagination';
import { openAlert, openConfirm, dateFormat } from '../../utils/common';
import systemAlertService from '../../services/systemAlertService';
import { sanitizeInput, validateNumericInput } from '../../utils/xssProtection';


const SystemAlertManagementPage = () => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [allChecked, setAllChecked] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create', 'edit'
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        message_type: 'login_banner',
        display_priority: 1,
        target_audience: 'admin',
        is_active: true,
        effective_from: '',
        effective_until: '',
        require_acknowledgment: true,
        display_duration: null
    });
    const [validationErrors, setValidationErrors] = useState({});

    // 페이징 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;
    const pageBlock = 5;

    useEffect(() => {
        loadAlerts();
    }, [currentPage]);

    const loadAlerts = async (page = 1) => {
        setLoading(true);
        try {
            const response = await systemAlertService.getAlerts({
                page: page,
                limit: pageSize
            });

            if (response.success) {
                setAlerts(response.data.alerts || []);
                setTotalCount(response.data.total || 0);
                setCurrentPage(page);
                setSelectedIds([]);
                setAllChecked(false);
            } else {
                openAlert({ title: '알림', description: response.message });
            }
        } catch (error) {
            openAlert({ title: '알림', description: '알림 목록을 불러오는 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    };

    // 체크박스 관련 핸들러
    const handleAllChange = e => {
        const checked = e.target.checked;
        setAllChecked(checked);
        setSelectedIds(checked ? alerts.map(item => item.id) : []);
    };

    const handleSelect = id => {
        let next = [...selectedIds];
        if (next.includes(id)) next = next.filter(x => x !== id);
        else next.push(id);
        setSelectedIds(next);
        setAllChecked(next.length === alerts.length);
    };

    // 모달 관련 핸들러
    const handleOpenCreate = () => {
        setFormData({
            title: '',
            message: '',
            message_type: 'login_banner',
            display_priority: 1,
            target_audience: 'admin',
            is_active: true,
            effective_from: '',
            effective_until: '',
            require_acknowledgment: true,
            display_duration: null
        });
        setModalMode('create');
        setShowModal(true);
    };

    const handleOpenEdit = alert => {
        setFormData({
            title: alert.title || '',
            message: alert.message || '',
            message_type: alert.messageType || 'login_banner',
            display_priority: alert.displayPriority || 1,
            target_audience: alert.targetAudience || 'admin',
            is_active: alert.isActive !== false,
            effective_from: alert.effectiveFrom || '',
            effective_until: alert.effectiveUntil || '',
            require_acknowledgment: alert.requireAcknowledgment !== false,
            display_duration: alert.displayDuration || null
        });
        setSelectedAlert(alert);
        setModalMode('edit');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedAlert(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let sanitizedValue = value;
        let validationError = null;
        
        if (type !== 'checkbox') {
            // textarea 필드는 줄바꿈을 보존하기 위해 기본 sanitizeInput 대신 제한적 정화 적용
            if (e.target.tagName.toLowerCase() === 'textarea') {
                // textarea는 HTML 태그만 제거하고 줄바꿈은 보존
                sanitizedValue = value
                    .replace(/<[^>]*>/g, '') // HTML 태그 제거
                    .replace(/javascript:/gi, '') // 자바스크립트 프로토콜 제거
                    .replace(/on\w+\s*=/gi, ''); // 이벤트 핸들러 제거
            } else {
                sanitizedValue = sanitizeInput(value);
            }
            
            // 필드별 검증
            if (name === 'display_priority') {
                const numValue = parseInt(sanitizedValue, 10);
                const validation = validateNumericInput(numValue, 1, 10);
                validationError = validation.isValid ? null : validation.error;
            } else if (name === 'display_duration' && sanitizedValue) {
                const numValue = parseInt(sanitizedValue, 10);
                const validation = validateNumericInput(numValue, 1, 3600);
                validationError = validation.isValid ? null : validation.error;
            } else if (name === 'title' && sanitizedValue.length > 200) {
                validationError = '제목은 200자를 초과할 수 없습니다.';
            } else if (name === 'message' && sanitizedValue.length > 2000) {
                validationError = '메시지는 2000자를 초과할 수 없습니다.';
            }
        }
        
        setValidationErrors(prev => ({
            ...prev,
            [name]: validationError
        }));
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : sanitizedValue
        }));
    };

    const handleCreateAlert = async () => {
        // 유효성 검사
        const hasErrors = Object.values(validationErrors).some(error => error !== null);
        if (hasErrors) {
            return openAlert({ title: '입력 오류', description: '입력값을 확인해주세요.' });
        }
        
        if (!formData.title.trim() || !formData.message.trim()) {
            return openAlert({ title: '알림', description: '제목과 메시지를 입력해주세요.' });
        }
        
        setLoading(true);
        try {
            const response = await systemAlertService.createAlert(formData);
            if (response.success) {
                openAlert({ title: '성공', description: response.message });
                handleCloseModal();
                loadAlerts(currentPage);
            } else {
                openAlert({ title: '알림', description: response.message });
            }
        } catch (error) {
            openAlert({ title: '알림', description: '알림 등록 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateAlert = async () => {
        // 유효성 검사
        const hasErrors = Object.values(validationErrors).some(error => error !== null);
        if (hasErrors) {
            return openAlert({ title: '입력 오류', description: '입력값을 확인해주세요.' });
        }
        
        if (!formData.title.trim() || !formData.message.trim()) {
            return openAlert({ title: '알림', description: '제목과 메시지를 입력해주세요.' });
        }
        
        setLoading(true);
        try {
            const response = await systemAlertService.updateAlert(selectedAlert.id, formData);
            if (response.success) {
                openAlert({ title: '성공', description: response.message });
                handleCloseModal();
                loadAlerts(currentPage);
            } else {
                openAlert({ title: '알림', description: response.message });
            }
        } catch (error) {
            openAlert({ title: '알림', description: '알림 수정 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    };

    // 대량 삭제 핸들러
    const handleBulkDeleteConfirm = () => {
        if (selectedIds.length === 0) {
            return openAlert({ title: '알림', description: '삭제할 알림을 선택해주세요.' });
        }
        openConfirm({
            title: '삭제하기',
            description: '선택된 알림을 삭제하시겠습니까?',
            positive_method: handleBulkDelete,
            positive_param: selectedIds,
            negative_method: null,
            negative_param: null,
            positive_btn: '확인',
            negative_btn: '취소',
        });
    };

    const handleBulkDelete = async (ids) => {
        setLoading(true);
        try {
            const promises = ids.map(id => systemAlertService.deleteAlert(id));
            await Promise.all(promises);
            openAlert({ title: '성공', description: '선택된 알림이 삭제되었습니다.' });
            setSelectedIds([]);
            setAllChecked(false);
            loadAlerts(currentPage);
        } catch (error) {
            openAlert({ title: '알림', description: '삭제 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (alertId, currentStatus) => {
        setLoading(true);
        try {
            const response = await systemAlertService.updateAlertStatus(alertId, !currentStatus);
            if (response.success) {
                openAlert({ title: '성공', description: response.message });
                loadAlerts(currentPage);
            } else {
                openAlert({ title: '알림', description: response.message });
            }
        } catch (error) {
            openAlert({ title: '알림', description: '상태 변경 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    };

    const getMessageTypeLabel = (type) => {
        const labels = {
            login_banner: '로그인 배너',
            system_notice: '시스템 공지',
            maintenance_alert: '점검 알림',
            security_warning: '보안 경고',
            security_notice: '보안 공지'
        };
        return labels[type] || type;
    };

    const getTargetAudienceLabel = (audience) => {
        const labels = {
            admin: '관리자',
            user: '사용자',
            device: '디바이스',
            all: '전체'
        };
        return labels[audience] || audience;
    };

    return (
      <SectionContainer title="시스템 알림 메시지 관리" className="list">
        <Spinner visible={loading} />
        <div className="search_area">
          <div className="acc_total">
            <h4>
              <b>{alerts.length}</b>개 검색되었습니다.
            </h4>
          </div>
          <div className="left">
            <div className="but-flex">
              <button
                className="btn btn-md primary mr-1"
                onClick={handleOpenCreate}
              >
                알림 등록
              </button>
              <button
                className="btn btn-lg tertiary"
                onClick={handleBulkDeleteConfirm}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
        <div className="content_area">
          <div className="table_area">
            <div className="table">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={handleAllChange}
                      />
                    </th>
                    <th>No</th>
                    <th>제목</th>
                    <th>메시지 타입</th>
                    <th>대상</th>
                    <th>우선순위</th>
                    <th>상태</th>
                    <th>시작일</th>
                    <th>종료일</th>
                    <th>수정</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert, idx) => (
                    <tr key={alert.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(alert.id)}
                          onChange={() => handleSelect(alert.id)}
                        />
                      </td>
                      <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                      <td>{alert.title}</td>
                      <td>{getMessageTypeLabel(alert.messageType)}</td>
                      <td>{getTargetAudienceLabel(alert.targetAudience)}</td>
                      <td>{alert.displayPriority}</td>
                      <td>
                        <div className="toggle_button">
                          <label className="label">
                            <input
                              className="toggle_input"
                              type="checkbox"
                              role="switch"
                              checked={alert.isActive}
                              onChange={() =>
                                handleToggleStatus(alert.id, alert.isActive)
                              }
                            />
                            <span></span>
                          </label>
                        </div>
                      </td>
                      <td>
                        {alert.effectiveFrom
                          ? dateFormat(alert.effectiveFrom)
                          : "-"}
                      </td>
                      <td>
                        {alert.effectiveUntil
                          ? dateFormat(alert.effectiveUntil)
                          : "-"}
                      </td>
                      <td>
                        <input
                          type="button"
                          className="btn fourbtn"
                          onClick={() => handleOpenEdit(alert)}
                          value="수정하기"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <Pagination
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          pageBlock={pageBlock}
          onPageChange={setCurrentPage}
        />

        {/* 등록/수정 모달 */}
        <Modal
          title={modalMode === "create" ? "알림 등록" : "알림 수정"}
          className="terminal-info mt-2"
          modalClassName="modal-ml"
          confirmLabel={modalMode === "create" ? "등록" : "수정"}
          visible={showModal}
          onConfirm={
            modalMode === "create" ? handleCreateAlert : handleUpdateAlert
          }
          onClose={handleCloseModal}
        >
          <dl>
            <dt>제목</dt>
            <dd>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                style={{
                  border: validationErrors.title
                    ? "2px solid #ff4444"
                    : "1px solid #ddd",
                }}
                maxLength="100"
              />
              {validationErrors.title && (
                <div
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {validationErrors.title}
                </div>
              )}
            </dd>
          </dl>
          <dl>
            <dt>메시지 내용</dt>
            <dd>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                style={{
                  width: "100%",
                  border: validationErrors.message
                    ? "2px solid #ff4444"
                    : "1px solid #ddd",
                }}
                maxLength="2000"
              />
              {validationErrors.message && (
                <div
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {validationErrors.message}
                </div>
              )}
            </dd>
          </dl>
          <dl>
            <dt>메시지 타입</dt>
            <dd>
              <select
                name="message_type"
                value={formData.message_type}
                onChange={handleInputChange}
              >
                <option value="login_banner">로그인 배너</option>
                <option value="system_notice">시스템 공지</option>
                <option value="maintenance_alert">점검 알림</option>
                <option value="security_warning">보안 경고</option>
                <option value="security_notice">보안 공지</option>
                <option value="compliance_warning">준수 경고</option>
                <option value="policy_update">정책 업데이트</option>
                <option value="service_announcement">서비스 공지</option>
              </select>
            </dd>
          </dl>
          <dl>
            <dt>대상 사용자</dt>
            <dd>
              <select
                name="target_audience"
                value={formData.target_audience}
                onChange={handleInputChange}
              >
                <option value="admin">관리자</option>
                <option value="user">사용자</option>
                <option value="device">디바이스</option>
                <option value="all">전체</option>
              </select>
            </dd>
          </dl>
          <dl>
            <dt>우선순위</dt>
            <dd>
              <input
                type="number"
                name="display_priority"
                value={formData.display_priority}
                onChange={handleInputChange}
                min="1"
                max="10"
                style={{
                  border: validationErrors.display_priority
                    ? "2px solid #ff4444"
                    : "1px solid #ddd",
                }}
              />
              {validationErrors.display_priority && (
                <div
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {validationErrors.display_priority}
                </div>
              )}
            </dd>
          </dl>
          <dl>
            <dt>시작일</dt>
            <dd>
              <input
                type="datetime-local"
                name="effective_from"
                value={formData.effective_from}
                onChange={handleInputChange}
              />
            </dd>
          </dl>
          <dl>
            <dt>종료일</dt>
            <dd>
              <input
                type="datetime-local"
                name="effective_until"
                value={formData.effective_until}
                onChange={handleInputChange}
              />
            </dd>
          </dl>
          <dl>
            <dt>표시 시간(초)</dt>
            <dd>
              <input
                type="number"
                name="display_duration"
                value={formData.display_duration || ""}
                onChange={handleInputChange}
                placeholder="비워두면 무제한 (1-3600초)"
                min="1"
                max="3600"
                style={{
                  border: validationErrors.display_duration
                    ? "2px solid #ff4444"
                    : "1px solid #ddd",
                }}
              />
              {validationErrors.display_duration && (
                <div
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    marginTop: "5px",
                  }}
                >
                  {validationErrors.display_duration}
                </div>
              )}
            </dd>
          </dl>
          <dl>
            <dt>설정</dt>
            <dd>
              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                활성화
              </label>
              <br />
              <label>
                <input
                  type="checkbox"
                  name="require_acknowledgment"
                  checked={formData.require_acknowledgment}
                  onChange={handleInputChange}
                />
                사용자 확인 필요
              </label>
            </dd>
          </dl>
        </Modal>
      </SectionContainer>
    );
};

export default SystemAlertManagementPage;
